// @flow
import {initialIntegrationCheckersData} from './initialData';
import type {IntegrationCheckersDataIntegrationCheckerResult} from '../../generatedTypes';

const POLL_DELAY = 15000;

class IntegrationCheckerRunner {
  _listeners: {
    data: Array<(IntegrationCheckersDataIntegrationCheckerResult) => void>,
    error: Array<(Event) => void>,
  };

  _pollCount: number;
  _loading: boolean;
  _intervalTask: any;
  _lastData: IntegrationCheckersDataIntegrationCheckerResult;

  constructor() {
    this._listeners = {data: [], error: []};
    this._loading = false;
    this._lastData = initialIntegrationCheckersData;
    this._pollCount = 0;
  }

  get isPolling() {
    return !!this._intervalTask;
  }

  onData(callBack: (IntegrationCheckersDataIntegrationCheckerResult) => void) {
    this._listeners.data.push(callBack);
  }

  onError(callBack: (Event) => void) {
    this._listeners.error.push(callBack);
  }

  _dispatchData(data: IntegrationCheckersDataIntegrationCheckerResult) {
    this._listeners.data.forEach((callback) => callback(data));
  }

  _dispatchError(data: Event) {
    this._listeners.error.forEach((callback) => callback(data));
  }

  runIntegrationChecker = (path: string, maxAttempts: number = Infinity) => {
    const metadata =
      document.head && document.head.querySelector("meta[name='csrf-token']");
    const token = metadata && metadata.getAttribute('content');

    this._loading = true;
    return window.$.ajax(`/integration-checker/${path}`, {
      type: 'GET',
      cache: false,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'x-stripe-csrf-token': token,
      },
      success: (data: IntegrationCheckersDataIntegrationCheckerResult) => {
        const isUpdatedData = this._isUpdatedData(this._lastData, data);
        this._loading = false;
        this._lastData = data;

        if (isUpdatedData && maxAttempts < Infinity) {
          this.stopPolling();
        }
        // We shouldn't have to do this after the Integration Checkers are
        // integrated to MarkDoc. Then IntegrationCheckerDataFetcher can call
        // stopPolling() on unmount.
        if (!window.location.href.includes('/docs/payments/accept-a-payment')) {
          this.stopPolling();
        }
        if (isUpdatedData || !this.isPolling) {
          this._dispatchData(data);
        }
      },
      error: (error) => {
        this.stopPolling();
        this._loading = false;
        this._dispatchError(error.responseJSON);
      },
    });
  };

  startPolling(path: string, maxAttempts: number = Infinity) {
    if (this._intervalTask) {
      return;
    }

    this._pollCount = 1;

    this._intervalTask = setInterval(() => {
      if (!this._loading && this._pollCount < maxAttempts) {
        this._pollCount += 1;
        this.runIntegrationChecker(path, maxAttempts);
      }

      if (this._pollCount >= maxAttempts) {
        this.stopPolling();
      }
    }, POLL_DELAY);
    this.runIntegrationChecker(path, maxAttempts);
  }

  _isUpdatedData(
    lastData: IntegrationCheckersDataIntegrationCheckerResult,
    currentData: IntegrationCheckersDataIntegrationCheckerResult,
  ) {
    return !!lastData.results.find((result, index) => {
      if (!currentData.results[index]) {
        return false;
      }
      return (
        (currentData.results[index].target_object_created !== undefined &&
          result &&
          result.target_object_created !==
            currentData.results[index].target_object_created) ||
        (currentData.results[index].success !== undefined &&
          result &&
          result.success !== currentData.results[index].success) ||
        (currentData.results[index].target_object_id !== undefined &&
          result &&
          result.target_object_id !==
            currentData.results[index].target_object_id)
      );
    });
  }

  stopPolling() {
    if (!this._intervalTask) {
      return;
    }
    clearInterval(this._intervalTask);
    this._intervalTask = null;
    this._pollCount = 0;
  }
}

export {POLL_DELAY};
export default IntegrationCheckerRunner;
